import React, { useState } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    FormControl,
    FormLabel,
    Input,
    VStack,
    HStack,
    useToast,
    Text,
    Icon,
    Box,
    SimpleGrid,
    Badge,
    Divider,
    Flex,
    Checkbox
} from '@chakra-ui/react';
import { FiCheckCircle, FiBookOpen } from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import { FaGraduationCap } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import registrationService from '../services/registrationService';

const MotionBox = motion(Box);

const getIconComponent = (iconName) => FaIcons[iconName] || FaGraduationCap;

const RegistrationModal = ({ isOpen, onClose, niveau }) => {
    const primaryColor = '#F2C919';
    const secondaryColor = '#2C3E50';

    const [formData, setFormData] = useState({
        parentName: '',
        studentName: '',
        parentPhone: '',
        studentPhone: '',
        email: ''
    });
    const [selectedMatieres, setSelectedMatieres] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const toast = useToast();

    const totalPrice = selectedMatieres.reduce((sum, m) => sum + (m.prix || 0), 0);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const toggleMatiere = (matiere) => {
        setSelectedMatieres(prev => {
            const exists = prev.find(m => m.name === matiere.name);
            if (exists) return prev.filter(m => m.name !== matiere.name);
            return [...prev, matiere];
        });
    };

    const handleSubmit = async (e) => {
        if (e) e.preventDefault();

        if (!formData.parentName || !formData.studentName || !formData.parentPhone || !formData.email) {
            toast({ title: 'يرجى ملء جميع الحقول المطلوبة', status: 'warning', duration: 3000 });
            return;
        }
        if (selectedMatieres.length === 0) {
            toast({ title: 'يرجى اختيار مادة واحدة على الأقل', status: 'warning', duration: 3000 });
            return;
        }

        setLoading(true);
        try {
            await registrationService.createRegistration({
                ...formData,
                niveau: niveau._id,
                selectedMatieres: selectedMatieres.map(m => ({ name: m.name, prix: m.prix || 0 })),
                totalPrice
            });

            setSubmitted(true);
            toast({
                title: 'تم إرسال طلب التسجيل بنجاح',
                description: 'سنتواصل معك قريباً',
                status: 'success',
                duration: 5000
            });

            setTimeout(() => {
                setFormData({ parentName: '', studentName: '', parentPhone: '', studentPhone: '', email: '' });
                setSelectedMatieres([]);
                setSubmitted(false);
                onClose();
            }, 2500);
        } catch (error) {
            toast({
                title: 'خطأ في إرسال الطلب',
                description: error.response?.data?.message || 'حدث خطأ',
                status: 'error',
                duration: 3000
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered scrollBehavior="inside">
            <ModalOverlay backdropFilter="blur(8px)" bg="blackAlpha.600" />
            <ModalContent dir="rtl" borderRadius="3xl" overflow="hidden" border="1px solid" borderColor="gray.100">

                {/* Colored Header */}
                <Box bg={`linear-gradient(135deg, ${secondaryColor}, #3d5a73)`} px={8} py={6}>
                    <HStack justify="space-between" align="center">
                        <VStack align="start" spacing={1}>
                            <Badge bg={primaryColor} color={secondaryColor} px={3} py={0.5} borderRadius="full" fontSize="xs" fontWeight="800">
                                طلب التسجيل
                            </Badge>
                            <Text fontSize="xl" fontWeight="900" color="white" fontFamily="Tajawal, sans-serif">
                                {submitted ? 'تم الإرسال بنجاح! 🎉' : `الانضمام إلى ${niveau?.name}`}
                            </Text>
                        </VStack>
                        <ModalCloseButton position="relative" top="0" right="0" color="white" _hover={{ bg: 'whiteAlpha.200' }} borderRadius="full" />
                    </HStack>
                </Box>

                <ModalBody py={6} px={8}>
                    {submitted ? (
                        <VStack spacing={6} py={10}>
                            <MotionBox
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                            >
                                <Box p={6} bg="green.50" borderRadius="full">
                                    <Icon as={FiCheckCircle} boxSize={16} color="green.500" />
                                </Box>
                            </MotionBox>
                            <VStack spacing={2} textAlign="center">
                                <Text fontSize="xl" fontFamily="Tajawal, sans-serif" fontWeight="900" color={secondaryColor}>
                                    تم استلام طلبك بنجاح
                                </Text>
                                <Text color="gray.500" fontFamily="Tajawal, sans-serif">
                                    سنتواصل معك في أقرب وقت ممكن
                                </Text>
                            </VStack>
                            <Box bg="yellow.50" border="1px dashed" borderColor={primaryColor} borderRadius="xl" p={4} w="full">
                                <Text fontWeight="800" color={secondaryColor} fontSize="sm" textAlign="center" fontFamily="Tajawal, sans-serif">
                                    المواد المختارة: {selectedMatieres.map(m => m.name).join('، ')}
                                </Text>
                                <Text fontWeight="900" color={secondaryColor} fontSize="lg" textAlign="center" fontFamily="Tajawal, sans-serif" mt={1}>
                                    المجموع: {totalPrice} DT / شهر
                                </Text>
                            </Box>
                        </VStack>
                    ) : (
                        <VStack spacing={6} align="stretch">

                            {/* Step 1: Subject Selection */}
                            <Box>
                                <HStack mb={3}>
                                    <Box w={7} h={7} bg={secondaryColor} borderRadius="full" display="flex" alignItems="center" justifyContent="center">
                                        <Text color="white" fontSize="xs" fontWeight="900">1</Text>
                                    </Box>
                                    <Text fontWeight="900" fontFamily="Tajawal, sans-serif" color={secondaryColor} fontSize="md">
                                        اختر المواد التي تريد الانتساب إليها
                                    </Text>
                                </HStack>

                                {niveau?.matieres?.length > 0 ? (
                                    <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                                        {niveau.matieres.map((matiere, idx) => {
                                            const MIcon = getIconComponent(matiere.icon);
                                            const isSelected = selectedMatieres.some(m => m.name === matiere.name);
                                            return (
                                                <MotionBox
                                                    key={idx}
                                                    whileHover={{ scale: 1.02 }}
                                                    whileTap={{ scale: 0.98 }}
                                                    onClick={() => toggleMatiere(matiere)}
                                                    cursor="pointer"
                                                >
                                                    <Box
                                                        p={4}
                                                        borderRadius="xl"
                                                        border="2px solid"
                                                        borderColor={isSelected ? primaryColor : 'gray.200'}
                                                        bg={isSelected ? `${primaryColor}15` : 'white'}
                                                        transition="all 0.2s"
                                                        boxShadow={isSelected ? `0 0 0 1px ${primaryColor}` : 'sm'}
                                                        position="relative"
                                                        overflow="hidden"
                                                    >
                                                        {isSelected && (
                                                            <Box position="absolute" top={2} left={2}>
                                                                <Icon as={FiCheckCircle} color={primaryColor} boxSize={4} />
                                                            </Box>
                                                        )}
                                                        <HStack spacing={3} align="center">
                                                            <Box
                                                                p={2}
                                                                bg={isSelected ? primaryColor : 'gray.100'}
                                                                borderRadius="lg"
                                                                color={isSelected ? secondaryColor : 'gray.500'}
                                                                transition="all 0.2s"
                                                            >
                                                                <Icon as={MIcon} boxSize={5} />
                                                            </Box>
                                                            <VStack align="start" spacing={0} flex={1}>
                                                                <Text fontWeight="800" fontFamily="Tajawal, sans-serif" fontSize="sm" color={secondaryColor} noOfLines={1}>
                                                                    {matiere.name}
                                                                </Text>
                                                                <Badge
                                                                    bg={isSelected ? secondaryColor : 'gray.100'}
                                                                    color={isSelected ? 'white' : 'gray.600'}
                                                                    borderRadius="full"
                                                                    px={2}
                                                                    fontSize="xs"
                                                                    fontWeight="700"
                                                                >
                                                                    {matiere.prix || 0} DT / شهر
                                                                </Badge>
                                                            </VStack>
                                                        </HStack>
                                                    </Box>
                                                </MotionBox>
                                            );
                                        })}
                                    </SimpleGrid>
                                ) : (
                                    <Box textAlign="center" py={6} bg="gray.50" borderRadius="xl">
                                        <Icon as={FiBookOpen} boxSize={8} color="gray.700" mb={2} />
                                        <Text color="gray.600" fontFamily="Tajawal, sans-serif" fontSize="sm">لا توجد مواد مضافة لهذا المستوى</Text>
                                    </Box>
                                )}

                                {/* Live Total */}
                                <AnimatePresence>
                                    {selectedMatieres.length > 0 && (
                                        <MotionBox
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                            mt={3}
                                        >
                                            <Box bg={secondaryColor} borderRadius="xl" p={4}>
                                                <HStack justify="space-between">
                                                    <VStack align="start" spacing={0}>
                                                        <Text color="whiteAlpha.700" fontSize="xs" fontFamily="Tajawal, sans-serif">
                                                            {selectedMatieres.length} مادة مختارة
                                                        </Text>
                                                        <Text color="white" fontSize="xs" fontFamily="Tajawal, sans-serif" noOfLines={1}>
                                                            {selectedMatieres.map(m => m.name).join('، ')}
                                                        </Text>
                                                    </VStack>
                                                    <VStack align="end" spacing={0}>
                                                        <Text color="whiteAlpha.700" fontSize="xs" fontFamily="Tajawal, sans-serif">المجموع الشهري</Text>
                                                        <Text color={primaryColor} fontSize="2xl" fontWeight="900" fontFamily="Tajawal, sans-serif" lineHeight="1">
                                                            {totalPrice} DT
                                                        </Text>
                                                    </VStack>
                                                </HStack>
                                            </Box>
                                        </MotionBox>
                                    )}
                                </AnimatePresence>
                            </Box>

                            <Divider />

                            {/* Step 2: Contact Info */}
                            <Box>
                                <HStack mb={4}>
                                    <Box w={7} h={7} bg={secondaryColor} borderRadius="full" display="flex" alignItems="center" justifyContent="center">
                                        <Text color="white" fontSize="xs" fontWeight="900">2</Text>
                                    </Box>
                                    <Text fontWeight="900" fontFamily="Tajawal, sans-serif" color={secondaryColor} fontSize="md">
                                        معلومات التواصل
                                    </Text>
                                </HStack>

                                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
                                    <FormControl isRequired>
                                        <FormLabel fontFamily="Tajawal, sans-serif" fontWeight="700" fontSize="sm" color="gray.600">اسم ولي الأمر</FormLabel>
                                        <Input
                                            name="parentName"
                                            value={formData.parentName}
                                            onChange={handleChange}
                                            placeholder="الاسم الكامل"
                                            size="md"
                                            borderRadius="lg"
                                            _focus={{ borderColor: primaryColor, boxShadow: `0 0 0 1px ${primaryColor}` }}
                                        />
                                    </FormControl>

                                    <FormControl isRequired>
                                        <FormLabel fontFamily="Tajawal, sans-serif" fontWeight="700" fontSize="sm" color="gray.600">اسم الطالب</FormLabel>
                                        <Input
                                            name="studentName"
                                            value={formData.studentName}
                                            onChange={handleChange}
                                            placeholder="الاسم الكامل"
                                            size="md"
                                            borderRadius="lg"
                                            _focus={{ borderColor: primaryColor, boxShadow: `0 0 0 1px ${primaryColor}` }}
                                        />
                                    </FormControl>

                                    <FormControl isRequired>
                                        <FormLabel fontFamily="Tajawal, sans-serif" fontWeight="700" fontSize="sm" color="gray.600">هاتف ولي الأمر</FormLabel>
                                        <Input
                                            name="parentPhone"
                                            value={formData.parentPhone}
                                            onChange={handleChange}
                                            placeholder="رقم الهاتف"
                                            type="tel"
                                            size="md"
                                            borderRadius="lg"
                                            _focus={{ borderColor: primaryColor, boxShadow: `0 0 0 1px ${primaryColor}` }}
                                        />
                                    </FormControl>

                                    <FormControl>
                                        <FormLabel fontFamily="Tajawal, sans-serif" fontWeight="700" fontSize="sm" color="gray.600">هاتف الطالب <Text as="span" color="gray.600" fontSize="xs">(اختياري)</Text></FormLabel>
                                        <Input
                                            name="studentPhone"
                                            value={formData.studentPhone}
                                            onChange={handleChange}
                                            placeholder="رقم الهاتف"
                                            type="tel"
                                            size="md"
                                            borderRadius="lg"
                                            _focus={{ borderColor: primaryColor, boxShadow: `0 0 0 1px ${primaryColor}` }}
                                        />
                                    </FormControl>

                                    <FormControl isRequired gridColumn={{ sm: 'span 2' }}>
                                        <FormLabel fontFamily="Tajawal, sans-serif" fontWeight="700" fontSize="sm" color="gray.600">البريد الإلكتروني</FormLabel>
                                        <Input
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            placeholder="example@email.com"
                                            type="email"
                                            size="md"
                                            borderRadius="lg"
                                            _focus={{ borderColor: primaryColor, boxShadow: `0 0 0 1px ${primaryColor}` }}
                                        />
                                    </FormControl>
                                </SimpleGrid>
                            </Box>
                        </VStack>
                    )}
                </ModalBody>

                {!submitted && (
                    <ModalFooter borderTop="1px" borderColor="gray.100" pt={4} px={8} pb={6}>
                        <HStack w="full" justify="space-between" align="center">
                            {selectedMatieres.length > 0 ? (
                                <Text fontWeight="900" fontFamily="Tajawal, sans-serif" color={secondaryColor}>
                                    المجموع: <Text as="span" color={primaryColor}>{totalPrice} DT</Text> / شهر
                                </Text>
                            ) : (
                                <Text fontSize="sm" color="gray.600" fontFamily="Tajawal, sans-serif">اختر مادة للمتابعة</Text>
                            )}
                            <HStack>
                                <Button variant="ghost" onClick={onClose} fontFamily="Tajawal, sans-serif" borderRadius="lg">
                                    إلغاء
                                </Button>
                                <Button
                                    bg={primaryColor}
                                    color={secondaryColor}
                                    _hover={{ bg: '#d4b016', transform: 'translateY(-2px)' }}
                                    _active={{ transform: 'translateY(0)' }}
                                    onClick={handleSubmit}
                                    isLoading={loading}
                                    loadingText="جاري الإرسال..."
                                    fontFamily="Tajawal, sans-serif"
                                    fontWeight="800"
                                    size="md"
                                    px={8}
                                    borderRadius="lg"
                                    boxShadow="md"
                                    isDisabled={selectedMatieres.length === 0}
                                >
                                    إرسال الطلب
                                </Button>
                            </HStack>
                        </HStack>
                    </ModalFooter>
                )}
            </ModalContent>
        </Modal>
    );
};

export default RegistrationModal;

